import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, UsersThree, Plus, Lock, GlobeHemisphereWest, EnvelopeSimple,
  Coins, Crown, Chalkboard, Briefcase, Church, Users,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

const TYPE_BADGE = {
  public:       { icon: GlobeHemisphereWest, label: "Public",  color: "bg-teal-500/20 text-teal-300" },
  private:      { icon: Lock,                label: "Private", color: "bg-white/10 text-cream/70" },
  invite_only:  { icon: EnvelopeSimple,      label: "Invite",  color: "bg-papaya/20 text-papaya" },
  paid:         { icon: Coins,               label: "Paid",    color: "bg-jam-gold/20 text-jam-gold" },
  subscription: { icon: Crown,               label: "Sub",     color: "bg-purple-500/20 text-purple-300" },
};

const CATEGORY_ICON = {
  conference: Briefcase, business: Briefcase, workshop: Chalkboard,
  class: Chalkboard, coaching: Users, meeting: UsersThree,
  church: Church, community: Users, creator: Crown, team: Users, hangout: Users,
};

export default function Rooms() {
  const nav = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/rooms", { params: filter ? { room_type: filter } : {} });
      setRooms(data || []);
    } catch (e) {
      toast.error("Could not load rooms");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="rooms-page">
      <div className="px-5 pt-6 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream" data-testid="rooms-back">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Communication Center</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">Rooms & Meetings</h1>
        </div>
        <Link
          to="/rooms/new"
          className="rounded-full bg-papaya text-ink text-[11px] px-4 py-2 font-black tracking-[0.18em] uppercase hover:brightness-110 flex items-center gap-1"
          data-testid="rooms-new-btn"
        >
          <Plus size={14} weight="bold" /> Start Room
        </Link>
      </div>

      {/* Type filter chips */}
      <div className="px-5 mt-5 flex gap-2 flex-wrap" data-testid="rooms-filters">
        {["", "public", "paid", "subscription", "invite_only"].map((t) => (
          <button
            key={t || "all"}
            onClick={() => setFilter(t)}
            className={`text-[10px] tracking-[0.25em] uppercase font-black px-3 py-1.5 rounded-full transition-colors ${
              filter === t ? "bg-papaya text-ink" : "bg-white/5 text-cream/60 hover:bg-white/10"
            }`}
            data-testid={`rooms-filter-${t || "all"}`}
          >{t || "All"}</button>
        ))}
      </div>

      {/* List */}
      <div className="px-5 mt-6">
        {loading ? (
          <p className="text-cream/40 text-xs tracking-[0.3em] uppercase animate-pulse">Loading rooms…</p>
        ) : rooms.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/15 p-8 text-center" data-testid="rooms-empty">
            <UsersThree size={40} className="text-cream/30 mx-auto" weight="duotone" />
            <p className="text-cream font-bold mt-3">No open rooms right now.</p>
            <p className="text-cream/50 text-xs mt-1">Be the first to spin up a workshop, class, or meeting.</p>
            <Link
              to="/rooms/new"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-papaya text-ink text-[11px] px-4 py-2 font-black tracking-[0.2em] uppercase"
              data-testid="rooms-empty-cta"
            >
              <Plus size={14} weight="bold" /> Start a Room
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {rooms.map((r) => <RoomCard key={r.id} room={r} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function RoomCard({ room }) {
  const badge = TYPE_BADGE[room.room_type] || TYPE_BADGE.public;
  const BadgeIcon = badge.icon;
  const CatIcon = CATEGORY_ICON[room.category] || UsersThree;
  return (
    <Link
      to={`/rooms/${room.id}`}
      className="rounded-3xl crystal-glass p-4 hover:border-papaya/40 transition-colors block"
      data-testid={`room-card-${room.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="w-11 h-11 rounded-2xl bg-papaya/15 text-papaya flex items-center justify-center">
          <CatIcon size={22} weight="fill" />
        </div>
        <span className={`text-[9px] tracking-[0.25em] uppercase font-black px-2 py-0.5 rounded-full ${badge.color} flex items-center gap-1`}>
          <BadgeIcon size={10} weight="fill" /> {badge.label}
        </span>
      </div>
      <h3 className="text-cream font-black mt-3 truncate">{room.title}</h3>
      <p className="text-cream/50 text-xs mt-1 line-clamp-2 min-h-[2rem]">{room.description || "—"}</p>
      <div className="flex items-center justify-between mt-3 text-[10px] tracking-[0.25em] uppercase">
        <span className="text-cream/60">host · {room.host_username || room.host_display_name}</span>
        <span className="text-papaya font-bold">{room.participants_count || 0} / {room.max_participants || 12}</span>
      </div>
      {room.room_type === "paid" && (
        <p className="text-jam-gold text-[10px] tracking-[0.25em] uppercase font-black mt-2 flex items-center gap-1">
          <Coins size={11} weight="fill" /> {room.price_waves} waves entry
        </p>
      )}
    </Link>
  );
}
